// FormField — labelled input with inline error.
//
// Per AGENTS.md §3 #15 + W2 §3.2 #28:
//   - Field-level error renders below input with `data-field="<name>"`
//     so error.code routing can target it from outside.
//   - data-testid set to `field-<name>` (public test contract).
//
// W56.0 G — the error is now **programmatically** attached to the input.
// Before this, the error paragraph existed but the `<input>` was never told
// about it: the tree had zero `aria-invalid` and one unrelated
// `aria-describedby`, so a screen reader reached the field, found it
// perfectly valid, and stopped — the red text below was invisible to it.
//
// Now the error paragraph carries `id="field-error-<name>"` (the same string
// as its `data-testid`, so every existing test selector still resolves) and
// the input points at it with `aria-describedby`. `Input` derives
// `aria-invalid` from its `invalid` prop, so the field is also marked
// invalid for assistive tech.
//
// There is **no timer** anywhere in this component: when the error goes
// away is the caller's decision, not the component's.

import { type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";

interface FormFieldProps {
  name: string;
  label: string;
  type?: "text" | "email" | "password" | "date" | "number";
  required?: boolean;
  hint?: string;
  autoComplete?: string;
  leadingIcon?: ReactNode;
  value: string;
  onChange: (next: string) => void;
  invalid?: boolean;
  errorMessage?: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  /** Upper-bound for `type="date"` (YYYY-MM-DD). */
  max?: string;
  /** Lower-bound for `type="date"` (YYYY-MM-DD). */
  min?: string;
  /**
   * HTML inputmode hint (e.g. ``"numeric"`` for the 6-digit invite code).
   * Defaults to undefined; the browser falls back to ``type="text"``.
   */
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "email" | "url" | "search";
  autoFocus?: boolean;
  disabled?: boolean;
  /** Pass-through of arbitrary HTML data-* attributes (e.g. data-testid). */
  [dataAttr: `data-${string}`]: unknown;
}

export function FormField({
  name,
  label,
  type = "text",
  required,
  hint,
  autoComplete,
  leadingIcon,
  value,
  onChange,
  invalid,
  errorMessage,
  placeholder,
  minLength,
  maxLength,
  max,
  min,
  inputMode,
  autoFocus,
  disabled,
  ...dataAttrs
}: FormFieldProps) {
  // Pull ``data-testid``/``data-field`` forwards; the rest fall through via spread.
  const passthrough: Record<string, unknown> = { ...dataAttrs };
  const dataTestid = (passthrough["data-testid"] as string | undefined) ?? `field-${name}`;
  delete passthrough["data-testid"];
  delete passthrough["data-field"];
  // The error paragraph's DOM id — deliberately the same string as its
  // `data-testid`, so the public test contract and the accessibility
  // reference cannot drift apart.
  const errorId = `field-error-${name}`;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`field-${name}`} required={required}>
        {label}
      </Label>
      <Input
        id={`field-${name}`}
        name={name}
        type={type}
        autoComplete={autoComplete}
        leadingIcon={leadingIcon}
        placeholder={placeholder}
        minLength={minLength}
        maxLength={maxLength}
        max={max}
        min={min}
        inputMode={inputMode}
        autoFocus={autoFocus}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={invalid}
        data-testid={dataTestid}
        data-field={name}
        required={required}
        {...passthrough}
        // After the spread on purpose: the derived reference wins, so a
        // stray `aria-describedby` in the passthrough cannot point the input
        // at the wrong element. With no error there is no description to
        // point at, so the attribute is omitted entirely rather than set to
        // an empty string (an empty `aria-describedby` is a dangling
        // reference).
        aria-describedby={errorMessage ? errorId : undefined}
      />
      {errorMessage ? (
        <p
          id={errorId}
          data-testid={errorId}
          data-field={name}
          role="alert"
          className="text-body-sm text-partner-strong"
        >
          {errorMessage}
        </p>
      ) : hint ? (
        <p className="text-body-sm text-ink-3">{hint}</p>
      ) : null}
    </div>
  );
}

export function FormErrorBanner({
  message,
  className,
}: {
  message: string | null | undefined;
  className?: string;
}) {
  if (!message) return null;
  return (
    <div
      data-testid="form-error-banner"
      role="alert"
      className={cn(
        "text-body-sm text-partner-strong bg-paper-calm border border-partner rounded-md px-3 py-2",
        className,
      )}
    >
      {message}
    </div>
  );
}
